import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { getFleetMediaUrl } from "../../../../../api/fleet-management/api";

const DriverForm = ({
  form,
  validationErrors,
  salaryLabel,
  submitting,
  isEditMode,
  onSubmit,
  onFieldChange,
  onNestedFieldChange,
  onUploadProfileImage,
  onRemoveProfileImage,
  isUploadingImage,
  isDeletingImage,
  onBack,
}) => {
  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Profile Photo</h5>
      <Row>
        <Col md={8}>
          <Form.Group className="mb-3">
            <Form.Label>Driver Photo</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              disabled={isUploadingImage || isDeletingImage || submitting}
              onChange={(e) => onUploadProfileImage(e.target.files?.[0])}
            />
            <Form.Text className="text-muted">
              Upload a profile photo for the driver.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {form.image && (
        <div className="border rounded p-3 mb-3 d-flex align-items-start justify-content-between gap-3">
          <div>
            <div className="text-muted small mb-2">Current profile photo</div>
            <img
              src={getFleetMediaUrl(form.image)}
              alt={`${form.name || "Driver"} profile`}
              style={{
                width: "160px",
                height: "110px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
              }}
            />
            <small className="text-muted d-block mt-2">{form.image}</small>
          </div>

          <Button
            type="button"
            variant="outline-danger"
            disabled={isDeletingImage || isUploadingImage || submitting}
            onClick={onRemoveProfileImage}
          >
            {isDeletingImage ? "Removing..." : "Remove Photo"}
          </Button>
        </div>
      )}

      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="Enter full name"
              isInvalid={!!validationErrors.name}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>CNIC</Form.Label>
            <Form.Control
              type="text"
              value={form.cnic}
              onChange={(e) => onFieldChange("cnic", e.target.value)}
              placeholder="37405-1234567-1"
              isInvalid={!!validationErrors.cnic}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.cnic}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Contact Number</Form.Label>
            <Form.Control
              type="text"
              value={form.contactNumber}
              onChange={(e) => onFieldChange("contactNumber", e.target.value)}
              placeholder="03001234567"
              isInvalid={!!validationErrors.contactNumber}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.contactNumber}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select value={form.status} onChange={(e) => onFieldChange("status", e.target.value)}>
              <option value="available">Available</option>
              <option value="booked">Booked</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">License Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>License Number</Form.Label>
            <Form.Control
              type="text"
              value={form.license.number}
              onChange={(e) => onNestedFieldChange("license", "number", e.target.value)}
              placeholder="RWP-DRV-1122"
              isInvalid={!!validationErrors["license.number"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["license.number"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>License Expiry Date</Form.Label>
            <Form.Control
              type="date"
              value={form.license.expiryDate}
              onChange={(e) => onNestedFieldChange("license", "expiryDate", e.target.value)}
              isInvalid={!!validationErrors["license.expiryDate"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["license.expiryDate"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Employment Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Salary Type</Form.Label>
            <Form.Select
              value={form.employment.salaryType}
              onChange={(e) => onNestedFieldChange("employment", "salaryType", e.target.value)}
            >
              <option value="per_day">Per Day</option>
              <option value="per_trip">Per Trip (ad-hoc)</option>
              <option value="per_month">Per Month (permanent/contract)</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{salaryLabel}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.employment.salaryAmount}
              onChange={(e) => onNestedFieldChange("employment", "salaryAmount", e.target.value)}
              placeholder="Enter amount"
              isInvalid={!!validationErrors["employment.salaryAmount"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["employment.salaryAmount"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 mt-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              {isEditMode ? "Saving..." : "Creating..."}
            </>
          ) : (
            isEditMode ? "Save Changes" : "Create Driver"
          )}
        </Button>
        <Button type="button" variant="light" disabled={submitting} onClick={onBack}>
          Back
        </Button>
      </div>
    </Form>
  );
};

export default DriverForm;
